/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import Vue from 'vue';
import VueRouter, { RouteConfig, RawLocation, Route } from 'vue-router';
import store from '@/renderer/store/index';
import { RoleMap } from '@/renderer/scripts/utils/ConstantConfig';
import { getUserRole, getOrgEdition } from '@/renderer/scripts/utils/UserRole';
import meeting from '@/renderer/scripts/utils/meeting';
import { meetingLoginInRouter } from '@/renderer/scripts/utils/meeting/autoLoginInMeeting';
import { DrawerEvents } from '@/renderer/store/modules/drawer';
import { cancelRequest } from '@/renderer/scripts/smh';
import { IS_BROWSER, IS_PC, IS_PRIVATE } from '@/common/constant/index';

Vue.use(VueRouter);

const login = () => import(/* webpackChunkName: "login" */ '@/renderer/views/login/index.vue');
const register = () => import(/* webpackChunkName: "register" */ '@/renderer/views/register/index.vue');
const idassLogin = () => import(/* webpackChunkName: "idassLogin" */ '@/renderer/views/login/idassLogin.vue');
const oauth2 = () => import(/* webpackChunkName: "oauth2" */ '@/renderer/views/login/oauth2.vue');
const ssoLogin = () => import(/* webpackChunkName: "ssoLogin" */ '@/renderer/views/login/ssoLogin.vue');
const desktop = () => import(/* webpackChunkName: "desktop" */ '@/renderer/views/desktop/index.vue');
const preview = () => import(/* webpackChunkName: "preview" */ '@/renderer/views/preview/index.vue');
const refresh = () => import(/* webpackChunkName: "preview" */ '@/renderer/views/refresh/index.vue');
const share = () => import(/* webpackChunkName: "share" */ '@/renderer/views/share/index.vue');
const collect = () => import(/* webpackChunkName: "collect" */ '@/renderer/views/common/collect/index.vue');

const loginWithApp = () => import(/* webpackChunkName: "loginWithApp" */ '@/renderer/views/login/loginWithApp.vue');
const teamSpace = () =>
  import(/* webpackChunkName: "teamSpace" */ '@/renderer/views/desktop/FileManagement/TeamSpace/index.vue');
const personalSpace = () =>
  import(/* webpackChunkName: "personalSpace" */ '@/renderer/views/desktop/FileManagement/PersonalSpace/index.vue');
const recycleBin = () =>
  import(/* webpackChunkName: "recycleBin" */ '@/renderer/views/desktop/FileManagement/RecycleBin/index.vue');
const transmission = () =>
  import(/* webpackChunkName: "transmission" */ '@/renderer/views/desktop/FileManagement/Transmission/index.vue');
const relatedToMe = () =>
  import(/* webpackChunkName: "relatedToMe" */ '@/renderer/views/desktop/FileManagement/RelatedToMe/index.vue');
const relatedToMeFileList = () =>
  import(
    /* webpackChunkName: "relatedToMeFileList" */ '@/renderer/views/desktop/FileManagement/RelatedToMe/FileList.vue'
  );
const fileApply = () =>
  import(/* webpackChunkName: "fileApply" */ '@/renderer/views/desktop/FileManagement/FileApply/index.vue');
// const receivedShare = () => import(/* webpackChunkName: "receivedShare" */ '@/renderer/views/desktop/FileManagement/shareManage/components/receivedFileList.vue');
const linkManage = () =>
  import(/* webpackChunkName: "linkManage" */ '@/renderer/views/desktop/FileManagement/LinkManage/index.vue');
const myFavorite = () =>
  import(/* webpackChunkName: "myFavorite" */ '@/renderer/views/desktop/FileManagement/Workbench/MyFavorite/index.vue');
const recentUse = () =>
  import(/* webpackChunkName: "recentUse" */ '@/renderer/views/desktop/FileManagement/Workbench/RecentUse/index.vue');
const transit = () =>
  import(/* webpackChunkName: "transit" */ '@/renderer/views/desktop/FileManagement/Transit/index.vue');
const collaborationSpace = () =>
  import(
    /* webpackChunkName: "collaborationSpace" */ '@/renderer/views/desktop/FileManagement/CollaborationSpace/index.vue'
  );
const collaborationSpaceList = () =>
  import(
    /* webpackChunkName: "collaborationSpaceList" */ '@/renderer/views/desktop/FileManagement/CollaborationSpace/SpaceList.vue'
  );
const collaborationShareList = () =>
  import(
    /* webpackChunkName: "collaborationShareList" */ '@/renderer/views/desktop/FileManagement/CollaborationSpace/LinkManage.vue'
  );
const linkInvitation = () =>
  import(/* webpackChunkName: "linkInvitation" */ '@/renderer/views/LinkInvitation/index.vue');
const favoriteList = () =>
  import(
    /* webpackChunkName: "favoriteList" */ '@/renderer/views/desktop/FileManagement/Workbench/MyFavorite/SpaceList.vue'
  );
const workbench = () =>
  import(/* webpackChunkName: "workbench" */ '@/renderer/views/desktop/FileManagement/Workbench/index.vue');
const electronicSign = () =>
  import(/* webpackChunkName: "electronicSign" */ '@/renderer/views/desktop/FileManagement/ElectronicSign/index.vue');
const suspiciousFile = () =>
  import(/* webpackChunkName: "suspiciousFile" */ '@/renderer/views/desktop/FileManagement/SuspiciousFile/index.vue');
const meetingAuth = () => import(/* webpackChunkName: "meetingAuth" */ '@/renderer/views/meetingAuth/index.vue');
const meetingLogin = () =>
  import(/* webpackChunkName: "meetingLogin" */ '@/renderer/views/meetingAuth/meetingLogin.vue');
const teamExpired = () =>
  import(/* webpackChunkName: "teamExpired" */ '@/renderer/views/desktop/TeamExpired/index.vue');
const search = () =>
  import(/* webpackChunkName: "search" */ '@/renderer/views/desktop/FileManagement/Search/index.vue');
const fileCollect = () =>
  import(/* webpackChunkName: "fileCollect" */ '@/renderer/views/desktop/FileManagement/FileCollect/index.vue');
const resetPassword = () => import(/* webpackChunkName: "resetPassword" */ '@/renderer/views/resetPassword/index.vue');

/**
 * @description 适用于路由的数组类型
 * 拓展了数个批量管理 meta 标签的方法
 */
class RouterArray extends Array<RouteConfig> {
  /**
   * @description 批量为数组类型的 meta 属性添加值
   * @param keyName 属性名
   * @param value 属性值
   * @returns this
   */
  addMetaArrayAttr(keyName: string, ...value: any) {
    this.forEach(item => {
      if (!item.meta) item.meta = {};
      if (!item.meta[keyName]) item.meta[keyName] = [];
      item.meta[keyName].push(...value);
    });
    return this;
  }
}

// 文件管理路由
const fileManagementRouters: Array<RouteConfig> = [
  {
    path: 'workbench',
    name: 'workbench',
    // 默认不加载tabs
    meta: {
      tabs: [],
      searchAvailable: true, // 目前只在工作台搜索添加全局搜索入口
    },

    component: workbench,
  },
  {
    path: 'personalSpace',
    name: 'personalSpace',
    meta: {
      name: 'personalSpace',
      searchAvailable: true,
      hasDrawer: true,
    },
    component: personalSpace,
  },
  {
    path: 'teamSpace',
    name: 'teamSpace',
    meta: {
      searchAvailable: true,
      auth: ['team'],
      hasDrawer: true,
    },
    component: teamSpace,
  },
  {
    path: 'recycleBin',
    name: 'recycleBin',
    component: recycleBin,
    // 回收站的抽屉目前是定制的
    meta: {
      hasDrawer: false,
    },
  },
  {
    path: 'transmission',
    name: 'transmission',
    component: transmission,
    meta: {
      hasDrawer: false,
    },
  },
  {
    path: 'relatedToMe',
    name: 'relatedToMe',
    component: relatedToMe,
    meta: {
      hasDrawer: false,
    },
  },
  {
    path: 'relatedToMeFileList',
    name: 'relatedToMeFileList',
    component: relatedToMeFileList,
    meta: {
      hasDrawer: true,
    },
  },
  {
    path: 'fileApply',
    name: 'fileApply',
    component: fileApply,
  },
  {
    path: 'linkManage',
    name: 'linkManage',
    component: linkManage,
  },
  {
    path: 'myFavorite',
    name: 'myFavorite',
    component: myFavorite,
  },
  {
    path: 'recentUse',
    name: 'recentUse',
    component: recentUse,
  },
  {
    path: 'transit',
    name: 'transit',
    component: transit,
  },
  {
    path: 'collaborationSpace',
    name: 'collaborationSpace',
    component: collaborationSpace,
    meta: {
      auth: ['collaboration'],
      hasDrawer: true,
    },
  },
  {
    path: 'collaborationSpaceList',
    name: 'collaborationSpaceList',
    meta: {
      searchAvailable: true,
      hasDrawer: true,
    },
    component: collaborationSpaceList,
  },
  {
    path: 'collaborationShareList',
    name: 'collaborationShareList',
    component: collaborationShareList,
  },
  {
    path: 'favoriteList',
    name: 'favoriteList',
    meta: {
      searchAvailable: true,
    },
    component: favoriteList,
  },
  {
    path: 'electronicSign',
    name: 'electronicSign',
    component: electronicSign,
    meta: {
      hasDrawer: false,
      editionAllowList: ['enterprise'],
    },
  },
  {
    path: 'suspiciousFile',
    name: 'suspiciousFile',
    component: suspiciousFile,
  },
  {
    path: 'search',
    name: 'search',
    component: search,
  },
  {
    path: 'fileCollect',
    name: 'fileCollect',
    component: fileCollect,
    meta: {
      hasDrawer: false,
      editionAllowList: ['enterprise'],
    },
  },
];

// PC 端才引入文件同步页
if (IS_PC) {
  // PC 端才引入文件同步页
  const fileSync = () =>
    import(/* webpackChunkName: "fileSync" */ '@/renderer/views/desktop/FileManagement/FileSync/index.vue');
  fileManagementRouters.push({
    path: 'fileSync',
    name: 'fileSync',
    component: fileSync,
  });
}
const userManagement = () =>
  import(/* webpackChunkName: "userManagement" */ '@/renderer/views/desktop/ManageConsole/UserManagement/index.vue');
const teamManagement = () =>
  import(/* webpackChunkName: "teamManagement" */ '@/renderer/views/desktop/ManageConsole/TeamManagement/index.vue');
const initializeSettings = () =>
  import(/* webpackChunkName: "logQuery" */ '@/renderer/views/desktop/ManageConsole/InitializeSettings/index.vue');
const uiDesign = () =>
  import(/* webpackChunkName: "uiDesign" */ '@/renderer/views/desktop/ManageConsole/UIDesign/index.vue');
const watermark = () =>
  import(/* webpackChunkName: "watermark" */ '@/renderer/views/desktop/ManageConsole/Watermark/index.vue');
const versionManagement = () =>
  import(
    /* webpackChunkName: "versionManagement" */ '@/renderer/views/desktop/ManageConsole/VersionManagement/index.vue'
  );
const securityScan = () =>
  import(/* webpackChunkName: "securityScan" */ '@/renderer/views/desktop/ManageConsole/SecurityScan/index.vue');
const logQuery = () =>
  import(/* webpackChunkName: "logQuery" */ '@/renderer/views/desktop/ManageConsole/LogQuery/index.vue');
const consoleLinkManagement = () =>
  import(
    /* webpackChunkName: "consoleLinkManagement" */ '@/renderer/views/desktop/ManageConsole/ConsoleLinkManagement/index.vue'
  );
const accountAssociation = () =>
  import(
    /* webpackChunkName: "accountAssociation" */ '@/renderer/views/desktop/ManageConsole/AccountAssociation/index.vue'
  );
const customDomain = () =>
  import(/* webpackChunkName: "customDomain" */ '@/renderer/views/desktop/ManageConsole/CustomDomain/index.vue');
const userStatistics = () =>
  import(
    /* webpackChunkName: "userStatistics" */ '@/renderer/views/desktop/ManageConsole/Statistics/UserStatistics/index.vue'
  );
const spaceStatistics = () =>
  import(
    /* webpackChunkName: "spaceStatistics" */ '@/renderer/views/desktop/ManageConsole/Statistics/SpaceStatistics/index.vue'
  );
const trafficStatistics = () =>
  import(
    /* webpackChunkName: "userStatistics" */ '@/renderer/views/desktop/ManageConsole/Statistics/TrafficStatistics/index.vue'
  );

// 管理控制台路由
export const manageConsoleRouters = new RouterArray(
  {
    path: 'userManagement',
    name: 'userManagement',
    component: userManagement,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'teamManagement',
    name: 'teamManagement',
    component: teamManagement,
    meta: {
      whiteList: [RoleMap.TeamAdmin],
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'uiDesign',
    name: 'uiDesign',
    component: uiDesign,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'watermark',
    name: 'watermark',
    component: watermark,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'versionManagement',
    name: 'versionManagement',
    component: versionManagement,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'securityScan',
    name: 'securityScan',
    component: securityScan,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'logQuery',
    name: 'logQuery',
    component: logQuery,
    meta: {
      whiteList: [RoleMap.TeamAdmin],
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'consoleLinkManagement',
    name: 'consoleLinkManagement',
    component: consoleLinkManagement,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'initializeSettings',
    name: 'initializeSettings',
    component: initializeSettings,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'accountAssociation',
    name: 'accountAssociation',
    component: accountAssociation,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'customDomain',
    name: 'customDomain',
    component: customDomain,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'userStatistics',
    name: 'userStatistics',
    component: userStatistics,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'spaceStatistics',
    name: 'spaceStatistics',
    component: spaceStatistics,
    meta: {
      editionAllowList: ['team', 'enterprise'],
    },
  },
  {
    path: 'trafficStatistics',
    name: 'trafficStatistics',
    component: trafficStatistics,
    meta: {
      editionAllowList: ['enterprise'],
    },
  },
).addMetaArrayAttr('whiteList', RoleMap.Admin, RoleMap.SuperAdmin);

// 文档预览路由
const viewFileRouters: Array<RouteConfig> = [
  {
    path: 'doc',
    name: 'doc',
    component: preview,
    meta: {
      hasDrawer: true,
    },
  },
  {
    path: 'edit',
    name: 'edit',
    component: preview,
    meta: {
      hasDrawer: true,
    },
  },
  {
    path: 'media',
    name: 'media',
    meta: {
      hasDrawer: true,
    },
    component: preview,
  },
];
const routes: Array<RouteConfig> = [
  {
    path: '/login',
    name: 'login',
    component: login,
    children: [
      {
        path: 'oauth2',
        name: 'oauth2',
        component: oauth2,
        meta: {
          isLoginPage: true,
        },
      },
      {
        path: 'register',
        name: 'register',
        component: register,
        meta: {
          isLoginPage: true,
        },
      },
      {
        path: 'sso',
        name: 'sso',
        component: ssoLogin,
        meta: {
          isLoginPage: true,
        },
      },
    ],
  },
  {
    path: '/web',
    name: 'web',
    component: {
      render: (h: any) => h('router-view'),
    },
    redirect: '/desktop',
    children: [
      {
        path: 'meetingLogin',
        name: 'meetingLogin',
        component: meetingLogin,
        meta: {
          isLoginPage: true,
        },
      },
      {
        path: 'idassLogin',
        name: 'idassLogin',
        component: idassLogin,
      },
      {
        path: 'desktop',
        name: 'desktop',
        component: desktop,
        redirect: 'workbench',
        children: [...fileManagementRouters, ...manageConsoleRouters],
      },
      {
        path: 'preview',
        name: 'preview',
        component: preview,
        children: viewFileRouters,
      },
      {
        path: 'refresh',
        name: 'refresh',
        component: refresh,
      },
      {
        path: 'share/:shareToken',
        name: 'share',
        component: share,
      },
      {
        path: 'collect/:collectToken',
        name: 'collect',
        component: collect,
      },
      {
        path: 'invitation/:code',
        name: 'invitation',
        component: linkInvitation,
      },
      {
        path: 'resetPassword/:token',
        name: 'resetPassword',
        component: resetPassword,
      },
      {
        path: 'meetingAuth',
        name: 'meetingAuth',
        component: meetingAuth,
      },
      {
        path: 'qr-connect/:id',
        name: 'qr-connect',
        component: loginWithApp,
      },
      {
        path: 'teamExpired',
        name: 'teamExpired',
        component: teamExpired,
      },
    ],
  },
  {
    path: '*',
    redirect: {
      name: 'login',
    },
  },
];
const router = new VueRouter({
  mode: process.env.IS_ELECTRON ? 'hash' : 'history',
  // base: process.env.BASE_URL,
  routes,
});
/**
 *  重置drawer抽屉内容区
 *  1. 首先需要先销毁抽屉，再创建一个新的抽屉
 */
const resetDrawer = (to: Route, from: Route) => {
  // 设置白名单，白名单路由需要负责正确的 初始化组件
  if (!to.meta?.hasDrawer) {
    store.commit(DrawerEvents.DESTORY_DRAWER);
  }
};

// 临时用户可访问白名单
const temporaryRouteList = [
  'login',
  'teamExpired',
  'workbench',
  'collaborationSpace',
  'collaborationSpaceList',
  'collaborationShareList',
  'relatedToMe',
  'relatedToMeFileList',
  'linkManage',
  'recycleBin',
  'suspiciousFile',
  'search',
  'fileApply',
  'transit',
  ...viewFileRouters.map(item => item.name),
  // 移动端
  'recentUse',
  'myFavorite',
  'favoriteList',
  // 外部打开
  'invitation',
  'share',
];

/**
 * 设置页面的 robots meta 标签
 * @param shouldBlock 是否阻止搜索引擎索引
 */
function setRobotsMetaTag(shouldBlock: boolean) {
  let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement;

  if (!robotsMeta) {
    robotsMeta = document.createElement('meta');
    robotsMeta.name = 'robots';
    document.head.appendChild(robotsMeta);
  }

  if (shouldBlock) {
    // 阻止搜索引擎索引、跟踪链接、缓存和显示摘要
    robotsMeta.content = 'noindex, nofollow, noarchive, nosnippet';
  } else {
    // 允许搜索引擎索引（对于非敏感页面）
    robotsMeta.content = 'index, follow';
  }
}

// 在路由切换的时候，尝试着去清理发现的TDesign弹窗的DOM元素，由于组件已经在vue框架销毁，而DOM未销毁，已经失联了，所以无法利用Vue进行清理
router.beforeEach(async (to, from, next) => {
  if (to?.query?.orgId) {
    to.query.spaceOrgId = to.query.orgId;
    delete to.query.orgId;
    // @ts-ignore
    next(to);
    return;
  }
  const { name: previousName, query: previousQuery, params: previousParams } = from;
  previousName &&
    store.commit('setPreviousRoute', {
      name: previousName,
      query: previousQuery,
      params: previousParams,
    });
  const { name, query, params, meta } = to;

  // SEO 保护：对于分享外链和文件收集页面，禁止搜索引擎索引
  const sensitiveRoutes = ['share', 'collect', 'invitation'];
  if (IS_BROWSER && sensitiveRoutes.includes(name as string)) {
    setRobotsMetaTag(true);
  } else if (IS_BROWSER) {
    setRobotsMetaTag(false);
  }
  const { isTemporary } = store.getters.userBaseInfo;
  if (isTemporary && !temporaryRouteList.includes(name as string) && store.getters.isBrowser) {
    // 临时用户，且不在白名单中，跳转到web版
    next({
      name: 'web',
    });
    return;
  }
  // 初始化时会从null->loigin,此时不记录
  const isInit = !from.name && name === 'login';
  if (name && !isInit) {
    store.commit('setRoute', {
      name,
      query,
      params,
    });
  }

  // 腾讯会议里处理
  if (meeting.isTMWebview) {
    resetDrawer(to, from);
    meetingLoginInRouter(to, from, next);
    return;
  }
  // 避免客户端初始化时设置login路由
  if (from.path === '/' && to.path === '/login') {
    next();
    return;
  }

  // electron遇到删除的路由时会自动跳转到 '/'导致出现空白页，过滤这个情况
  // 标志为 path = '/' 且名字不为空，匹配的路由matched为空
  if (process.env.IS_ELECTRON && name && to.path === '/' && to.matched.length === 0) {
    // 中断当前的导航
    next({
      name: 'web',
    });
    return;
  }
  // 过滤版本白名单（个人版、团队版、企业版）
  if (meta?.editionAllowList) {
    const editionFlag = getOrgEdition();
    if (!meta.editionAllowList.includes(editionFlag) && store.getters.isBrowser) {
      // 中断当前的导航
      next({
        name: 'web',
      });
      return;
    }
  }
  // 过滤角色权限白名单
  if (meta?.whiteList) {
    const userRole = store.getters.userRole || (await getUserRole());
    if (!meta.whiteList.includes(userRole) && store.getters.isBrowser) {
      // 中断当前的导航
      next({
        name: 'web',
      });
      return;
    }
  }
  if (IS_PRIVATE && to.name === 'register') {
    // 私有化不允许进入注册页面
    next({ name: 'login' });
  }

  // 上报菜单pv
  if (from.name !== name) {
    reporter.report('menu_pv', {
      menu: name,
    });
  }
  resetDrawer(to, from);
  if (to.name !== 'login') {
    cancelRequest.cancel();
  }
  next();
});
export default router;
